// no-dd-sa:typescript-best-practices/boolean-prop-naming
import { FormEvent, useMemo, useState } from 'react';
import { X, CheckCircle2, FileText, BarChart3, Mail } from 'lucide-react';
import { marketingApi } from '@/lib/api/marketing';
import type { BenchmarkRequest, PlaybookRequest } from '@/types/marketing';
import { logError } from '@/lib/logger';

type LeadMagnetType = 'playbook' | 'benchmark';

type LeadMagnetModalProps = {
  open: boolean;
  onClose: () => void;
  type: LeadMagnetType;
};

const LEAD_MAGNET_CONFIG = {
  playbook: {
    icon: FileText,
    title: 'Engineering Metrics Playbook',
    subtitle: 'The complete guide to implementing engineering metrics without surveillance culture',
    description:
      "Enter your email and we'll send you the playbook immediately. No spam, just actionable insights.",
    buttonText: 'Send me the Playbook',
    successTitle: 'Playbook on its way!',
    successMessage: "Check your inbox in a few minutes. We've also included a few bonus resources.",
  },
  benchmark: {
    icon: BarChart3,
    title: '2025 Engineering Benchmark Report',
    subtitle: 'Cycle time and review benchmarks from 802,979 merged pull requests',
    description:
      "Enter your email and we'll send you the full report. See how your team compares.",
    buttonText: 'Send me the Report',
    successTitle: 'Report on its way!',
    successMessage: "Check your inbox in a few minutes. You'll also get our analysis of the key trends.",
  },
};

const teamSizeOptions = ['1-49', '50-99', '100-249', '250-499', '500+'];

export default function LeadMagnetModal({ open, onClose, type }: LeadMagnetModalProps) {
  const config = LEAD_MAGNET_CONFIG[type];
  const Icon = config.icon;

  const initialSource = useMemo(
    () => (typeof window !== 'undefined' ? window.location.href : ''),
    []
  );

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    team_size: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return null;
  }

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const payload: PlaybookRequest | BenchmarkRequest = {
        ...formData,
        source_url: initialSource,
      };

      if (type === 'playbook') {
        await marketingApi.requestPlaybook(payload as PlaybookRequest);
      } else {
        await marketingApi.requestBenchmark(payload as BenchmarkRequest);
      }

      // If we get here without error, submission was successful
      setStatus('success');
    } catch (err) {
      logError('Failed to submit lead magnet form', err);
      setError('Something went wrong. Please try again or email hello@codepulsehq.com.');
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setFormData({
      name: '',
      email: '',
      company: '',
      role: '',
      team_size: '',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-sm">
      <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl dark:bg-gray-900">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {status === 'success' ? (
          <div className="space-y-6 py-4">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 rounded-full bg-emerald-100 p-3 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {config.successTitle}
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">{config.successMessage}</p>
            </div>

            <div className="rounded-2xl bg-gray-50 p-4 dark:bg-gray-800">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Sent to: {formData.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Check your spam folder if you don&apos;t see it
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-xl bg-primary-600 px-6 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition hover:bg-primary-500"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-primary-100 p-3 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {config.title}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{config.subtitle}</p>
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">{config.description}</p>

            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="lm-name"
                    className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lm-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label
                    htmlFor="lm-email"
                    className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Work email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lm-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="lm-company"
                    className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Company
                  </label>
                  <input
                    id="lm-company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    placeholder="Acme Inc"
                  />
                </div>
                <div>
                  <label
                    htmlFor="lm-team-size"
                    className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Team size
                  </label>
                  <select
                    id="lm-team-size"
                    value={formData.team_size}
                    onChange={(e) => handleChange('team_size', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="">Select</option>
                    {teamSizeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option} engineers
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                {error}
              </p>
            )}

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-primary-600 px-6 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition hover:bg-primary-500 disabled:opacity-60"
              >
                {isSubmitting ? 'Sending...' : config.buttonText}
              </button>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
